import { useAppLanguage } from '@/hooks/useAppLanguage';
import Colors from '@/constants/colors';
import DetailPageLayout from '@/components/DetailPageLayout';
import DetailPageHeader from '@/components/DetailPageHeader';
import { getTranslation } from '@/constants/translations';

const content = {
  en: {
    sections: [
      {
        title: 'Language Settings',
        items: [
          {
            question: 'How do I change the app language?',
            answer: 'Go to Settings and tap on "Language". You can choose between English, Russian, and Polish. The change takes effect immediately.',
          },
          {
            question: 'What languages are available?',
            answer: 'Currently, Umland supports three languages: English, Russian (Русский), and Polish (Polski). We plan to add more languages in the future.',
          },
        ],
      },
      {
        title: 'Currency Settings',
        items: [
          {
            question: 'How do I change the default currency?',
            answer: 'Go to Settings and tap on "Currency". Select your preferred currency from the list. This will be used as the default in the Currency widget on the Home screen.',
          },
          {
            question: 'What currencies are available?',
            answer: 'You can choose from: US Dollar (USD), Euro (EUR), British Pound (GBP), Danish Krone (DKK), Norwegian Krone (NOK), Swedish Krona (SEK), Polish Zloty (PLN), and Turkish Lira (TRY).',
          },
          {
            question: 'How does the currency widget work?',
            answer: 'The Currency widget shows the exchange rate from Icelandic Krona (ISK) to your selected currency. Rates are updated regularly from public exchange rate APIs.',
          },
        ],
      },
      {
        title: 'Home Screen Widgets',
        items: [
          {
            question: 'What widgets are available?',
            answer: 'Umland offers 5 widgets: Currency (exchange rates), Weather (current weather in Reykjavik), Calendar (today\'s date), Holidays (upcoming Icelandic holidays), and Word of the Day (learn Icelandic vocabulary).',
          },
          {
            question: 'How do I enable or disable widgets?',
            answer: 'Go to Settings > Manage Widgets. Toggle the switch next to each widget to enable or disable it. Disabled widgets won\'t appear on the Home screen.',
          },
          {
            question: 'How do I reorder widgets?',
            answer: 'On the Home screen, you can drag and drop widgets to rearrange them. Press and hold a widget, then drag it to your desired position.',
          },
        ],
      },
      {
        title: 'Favorites',
        items: [
          {
            question: 'How do I add something to favorites?',
            answer: 'When viewing an article or place, tap the heart icon to add it to your favorites. Tap again to remove it.',
          },
          {
            question: 'Where can I find my favorites?',
            answer: 'Tap the heart icon in the bottom navigation bar to access your saved favorites. You\'ll see all your saved articles and places there.',
          },
        ],
      },
      {
        title: 'Offline Mode',
        items: [
          {
            question: 'Does the app work offline?',
            answer: 'Yes! Umland is designed to work offline. Previously loaded content is cached and available without an internet connection.',
          },
          {
            question: 'When does data refresh?',
            answer: 'When you\'re online, data refreshes automatically in the background. Weather and currency rates update more frequently than articles and places.',
          },
          {
            question: 'How do I install the app on my phone?',
            answer: 'Umland is a Progressive Web App (PWA). On iOS, tap the Share button in Safari and select "Add to Home Screen". On Android, tap the menu and select "Install app" or "Add to Home Screen".',
          },
        ],
      },
    ],
  },
  ru: {
    sections: [
      {
        title: 'Настройки языка',
        items: [
          {
            question: 'Как изменить язык приложения?',
            answer: 'Перейдите в Настройки и нажмите на "Язык". Вы можете выбрать английский, русский или польский. Изменение вступает в силу немедленно.',
          },
          {
            question: 'Какие языки доступны?',
            answer: 'В настоящее время Umland поддерживает три языка: английский (English), русский и польский (Polski). В будущем мы планируем добавить больше языков.',
          },
        ],
      },
      {
        title: 'Настройки валюты',
        items: [
          {
            question: 'Как изменить валюту по умолчанию?',
            answer: 'Перейдите в Настройки и нажмите на "Валюта". Выберите предпочитаемую валюту из списка. Она будет использоваться по умолчанию в виджете валюты на главном экране.',
          },
          {
            question: 'Какие валюты доступны?',
            answer: 'Вы можете выбрать: доллар США (USD), евро (EUR), британский фунт (GBP), датская крона (DKK), норвежская крона (NOK), шведская крона (SEK), польский злотый (PLN) и турецкая лира (TRY).',
          },
          {
            question: 'Как работает виджет валюты?',
            answer: 'Виджет валюты показывает курс обмена исландской кроны (ISK) к выбранной вами валюте. Курсы регулярно обновляются из публичных API обменных курсов.',
          },
        ],
      },
      {
        title: 'Виджеты главного экрана',
        items: [
          {
            question: 'Какие виджеты доступны?',
            answer: 'Umland предлагает 5 виджетов: Валюта (курсы обмена), Погода (текущая погода в Рейкьявике), Календарь (сегодняшняя дата), Праздники (предстоящие исландские праздники) и Слово дня (изучение исландской лексики).',
          },
          {
            question: 'Как включить или отключить виджеты?',
            answer: 'Перейдите в Настройки > Управление виджетами. Переключите тумблер рядом с каждым виджетом, чтобы включить или отключить его. Отключенные виджеты не будут отображаться на главном экране.',
          },
          {
            question: 'Как изменить порядок виджетов?',
            answer: 'На главном экране вы можете перетаскивать виджеты для изменения их порядка. Нажмите и удерживайте виджет, затем перетащите его в нужное положение.',
          },
        ],
      },
      {
        title: 'Избранное',
        items: [
          {
            question: 'Как добавить что-то в избранное?',
            answer: 'При просмотре статьи или места нажмите на значок сердца, чтобы добавить в избранное. Нажмите еще раз, чтобы удалить.',
          },
          {
            question: 'Где найти мое избранное?',
            answer: 'Нажмите на значок сердца в нижней панели навигации, чтобы получить доступ к сохраненному избранному. Там вы увидите все сохраненные статьи и места.',
          },
        ],
      },
      {
        title: 'Офлайн режим',
        items: [
          {
            question: 'Работает ли приложение офлайн?',
            answer: 'Да! Umland разработан для работы офлайн. Ранее загруженный контент кэшируется и доступен без подключения к интернету.',
          },
          {
            question: 'Когда обновляются данные?',
            answer: 'Когда вы онлайн, данные автоматически обновляются в фоновом режиме. Погода и курсы валют обновляются чаще, чем статьи и места.',
          },
          {
            question: 'Как установить приложение на телефон?',
            answer: 'Umland - это прогрессивное веб-приложение (PWA). На iOS нажмите кнопку "Поделиться" в Safari и выберите "На экран Домой". На Android нажмите меню и выберите "Установить приложение" или "Добавить на главный экран".',
          },
        ],
      },
    ],
  },
  pl: {
    sections: [
      {
        title: 'Ustawienia języka',
        items: [
          {
            question: 'Jak zmienić język aplikacji?',
            answer: 'Przejdź do Ustawień i dotknij "Język". Możesz wybrać między angielskim, rosyjskim i polskim. Zmiana wchodzi w życie natychmiast.',
          },
          {
            question: 'Jakie języki są dostępne?',
            answer: 'Obecnie Umland obsługuje trzy języki: angielski (English), rosyjski (Русский) i polski. Planujemy dodać więcej języków w przyszłości.',
          },
        ],
      },
      {
        title: 'Ustawienia waluty',
        items: [
          {
            question: 'Jak zmienić domyślną walutę?',
            answer: 'Przejdź do Ustawień i dotknij "Waluta". Wybierz preferowaną walutę z listy. Będzie ona używana jako domyślna w widżecie waluty na ekranie głównym.',
          },
          {
            question: 'Jakie waluty są dostępne?',
            answer: 'Możesz wybrać: dolar amerykański (USD), euro (EUR), funt brytyjski (GBP), korona duńska (DKK), korona norweska (NOK), korona szwedzka (SEK), złoty polski (PLN) i lira turecka (TRY).',
          },
          {
            question: 'Jak działa widżet waluty?',
            answer: 'Widżet waluty pokazuje kurs wymiany korony islandzkiej (ISK) na wybraną przez Ciebie walutę. Kursy są regularnie aktualizowane z publicznych API kursów wymiany.',
          },
        ],
      },
      {
        title: 'Widżety ekranu głównego',
        items: [
          {
            question: 'Jakie widżety są dostępne?',
            answer: 'Umland oferuje 5 widżetów: Waluta (kursy wymiany), Pogoda (aktualna pogoda w Reykjaviku), Kalendarz (dzisiejsza data), Święta (nadchodzące islandzkie święta) i Słowo dnia (nauka słownictwa islandzkiego).',
          },
          {
            question: 'Jak włączyć lub wyłączyć widżety?',
            answer: 'Przejdź do Ustawienia > Zarządzaj widżetami. Przełącz przełącznik obok każdego widżetu, aby go włączyć lub wyłączyć. Wyłączone widżety nie pojawią się na ekranie głównym.',
          },
          {
            question: 'Jak zmienić kolejność widżetów?',
            answer: 'Na ekranie głównym możesz przeciągać i upuszczać widżety, aby je przestawiać. Naciśnij i przytrzymaj widżet, a następnie przeciągnij go w wybrane miejsce.',
          },
        ],
      },
      {
        title: 'Ulubione',
        items: [
          {
            question: 'Jak dodać coś do ulubionych?',
            answer: 'Podczas przeglądania artykułu lub miejsca dotknij ikony serca, aby dodać do ulubionych. Dotknij ponownie, aby usunąć.',
          },
          {
            question: 'Gdzie mogę znaleźć moje ulubione?',
            answer: 'Dotknij ikony serca w dolnym pasku nawigacji, aby uzyskać dostęp do zapisanych ulubionych. Zobaczysz tam wszystkie zapisane artykuły i miejsca.',
          },
        ],
      },
      {
        title: 'Tryb offline',
        items: [
          {
            question: 'Czy aplikacja działa offline?',
            answer: 'Tak! Umland jest zaprojektowany do pracy offline. Wcześniej załadowana zawartość jest buforowana i dostępna bez połączenia z internetem.',
          },
          {
            question: 'Kiedy dane są odświeżane?',
            answer: 'Gdy jesteś online, dane odświeżają się automatycznie w tle. Pogoda i kursy walut aktualizują się częściej niż artykuły i miejsca.',
          },
          {
            question: 'Jak zainstalować aplikację na telefonie?',
            answer: 'Umland to progresywna aplikacja internetowa (PWA). Na iOS dotknij przycisku Udostępnij w Safari i wybierz "Dodaj do ekranu początkowego". Na Androidzie dotknij menu i wybierz "Zainstaluj aplikację" lub "Dodaj do ekranu głównego".',
          },
        ],
      },
    ],
  },
};

export default function HelpPage() {
  const { language } = useAppLanguage();
  const t = content[language] || content.en;
  const title = getTranslation('help', language);

  return (
    <DetailPageLayout>
      <DetailPageHeader title={title} />
      <div style={styles.content}>
        {t.sections.map((section, sectionIndex) => (
          <section key={sectionIndex} style={styles.section}>
            <h2 style={styles.sectionTitle}>{section.title}</h2>
            {section.items.map((item, itemIndex) => (
              <div key={itemIndex} style={styles.faqItem}>
                <h3 style={styles.question}>{item.question}</h3>
                <p style={styles.answer}>{item.answer}</p>
              </div>
            ))}
          </section>
        ))}
      </div>
    </DetailPageLayout>
  );
}

const styles = {
  content: {
    backgroundColor: Colors.background,
    minHeight: '100vh',
    padding: '24px',
  } as React.CSSProperties,

  section: {
    marginBottom: '32px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '16px',
    paddingBottom: '8px',
    borderBottom: `1px solid ${Colors.border}`,
  } as React.CSSProperties,

  faqItem: {
    marginBottom: '20px',
  } as React.CSSProperties,

  question: {
    fontSize: '16px',
    fontWeight: '600',
    color: Colors.text,
    marginBottom: '8px',
  } as React.CSSProperties,

  answer: {
    fontSize: '15px',
    color: Colors.textSecondary,
    lineHeight: '24px',
    margin: 0,
  } as React.CSSProperties,
};
